import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { businessUnitsApi } from '@/lib/api';
import { unitErrorMessage, useCanHandleRole, useCanManageUnits } from '@/lib/business-units';
import { useMe } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { ConfirmDialog } from './confirm-dialog';
import { UnitRoleSelect } from './unit-role-select';

/**
 * Whether the caller may change roles in `businessUnitId`: a Super Admin, or
 * someone who works in that unit and edits employees (its Administrator). The
 * server decides; this only keeps a control the server would refuse out of
 * sight.
 */
export function useCanChangeRolesIn(businessUnitId: string): boolean {
  const { businessUnits } = useMe();
  const canEditUsers = useHasPermission('user.edit');
  const canManageUnits = useCanManageUnits();
  if (canManageUnits) return true;
  return canEditUsers && businessUnits.some((u) => u.id === businessUnitId);
}

/** Whether the caller may give or take this unit role (see
 *  `useCanHandleRole`); false until the unit's roles have loaded. */
function useCanHandleUnitRole(businessUnitId: string, systemRoleId: string): boolean {
  const canHandle = useCanHandleRole();
  const roles = useQuery({
    queryKey: queryKeys.businessUnits.roles(businessUnitId),
    queryFn: () => businessUnitsApi.roles(businessUnitId),
  });
  return canHandle(roles.data?.data.find((r) => r.id === systemRoleId));
}

/** The role someone acts under in one unit — changed in place, one
 *  operation per change. Read-only text for a caller who cannot change it. */
export function UnitRoleCell(props: {
  businessUnitId: string;
  userId: string;
  systemRoleId: string;
  systemRoleName: string;
  /** They reach this unit through their own role's All Business Units — that
   *  role is their primary employer's, changed there, not here. */
  reachesAllUnits?: boolean;
}) {
  const canHandle = useCanHandleUnitRole(props.businessUnitId, props.systemRoleId);
  const canChange =
    useCanChangeRolesIn(props.businessUnitId) && !props.reachesAllUnits && canHandle;
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (systemRoleId: string) =>
      businessUnitsApi.changeRole(props.businessUnitId, props.userId, systemRoleId),
    onSuccess: () => {
      toast.success('Role changed');
      queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(props.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.me() });
    },
    onError: (err) => toast.error(unitErrorMessage(err, 'Could not change the role.')),
  });
  if (!canChange) return <span>{props.systemRoleName}</span>;
  return (
    <UnitRoleSelect
      businessUnitId={props.businessUnitId}
      value={props.systemRoleId}
      onChange={(id) => {
        if (id !== props.systemRoleId) change.mutate(id);
      }}
      disabled={change.isPending}
      className="h-8 w-44"
      aria-label="Role in this unit"
    />
  );
}

/** Remove someone's GRANTED access to a unit, after a confirmation. For
 *  whoever manages Business Units and may take that role away; refused by the
 *  server while they serve one of its customers. */
export function RevokeUnitAccessButton(props: {
  businessUnitId: string;
  unitName: string;
  userId: string;
  userName: string;
  /** The role the grant gives them. */
  systemRoleId: string;
}) {
  const canHandle = useCanHandleUnitRole(props.businessUnitId, props.systemRoleId);
  const canManageUnits = useCanManageUnits() && canHandle;
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => businessUnitsApi.revoke(props.businessUnitId, props.userId),
    onSuccess: () => {
      toast.success('Access removed');
      queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all });
      setOpen(false);
    },
    onError: (err) => {
      toast.error(unitErrorMessage(err, 'Could not remove access.'));
      setOpen(false);
    },
  });
  if (!canManageUnits) return null;
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Remove access
      </Button>
      <ConfirmDialog
        open={open}
        title="Remove access?"
        description={`${props.userName} will no longer be able to work in ${props.unitName}.`}
        confirmLabel="Remove access"
        destructive
        confirmDisabled={revoke.isPending}
        onConfirm={() => revoke.mutate()}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
